export async function fetchJSON<T>(path: string): Promise<T> {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`${path} returned ${res.status}`);
  return res.json() as Promise<T>;
}

export interface MarketSnapshot {
  quotes: Array<{
    symbol: string;
    asset_class: string;
    price: number;
    change_pct: number;
    source: string;
    updated_at: string;
    delayed: boolean;
  }>;
  source: string;
  updated_at: string;
  delayed: boolean;
}

export interface CalendarResponse {
  events: Array<{
    id: string;
    datetime_utc: string;
    country: string;
    name: string;
    category: string;
    impact: "low" | "medium" | "high";
    affected_assets: string[];
    source: string;
    updated_at: string;
    delayed: boolean;
  }>;
  source: string;
  updated_at: string;
  delayed: boolean;
}

export interface PositioningResponse {
  positions: Array<{
    instrument: string;
    net_spec: number;
    percentile: number;
    zscore: number;
    weekly_delta: number;
    source: string;
    updated_at: string;
    delayed: boolean;
  }>;
  source: string;
  updated_at: string;
  delayed: boolean;
}

export interface LiquidityResponse {
  scope: "US" | "G3";
  net_liq: number;
  chg_4w: number;
  chg_13w: number;
  proxy: true;
  source: string;
  updated_at: string;
  delayed: boolean;
}

export interface GeoResponse {
  trackers: Array<{
    scope: string;
    value: number;
    trend: "rising" | "falling" | "stable";
    tone_series: { date: string; value: number }[];
    vol_series: { date: string; value: number }[];
    top_events: Array<{
      seendate: string;
      title: string;
      url: string;
      domain: string;
      sourcecountry: string;
      tone: number;
      affected_assets: string[];
    }>;
    coverage_gap?: string;
    low_confidence?: boolean;
    source: string;
    updated_at: string;
    delayed: boolean;
  }>;
  source: string;
  updated_at: string;
  delayed: boolean;
}

export function fetchMarketSnapshot() {
  return fetchJSON<MarketSnapshot>("/api/markets/snapshot");
}

export function fetchCalendar() {
  return fetchJSON<CalendarResponse>("/api/calendar");
}

export function fetchPositioning() {
  return fetchJSON<PositioningResponse>("/api/positioning");
}

export function fetchLiquidity() {
  return fetchJSON<LiquidityResponse>("/api/liquidity");
}

export function fetchGeo() {
  return fetchJSON<GeoResponse>("/api/geo/timeline");
}

export type TrackerResponse = GeoResponse["trackers"][number];

export function fetchTracker(id: string) {
  return fetchJSON<TrackerResponse>(`/api/geo/tracker/${encodeURIComponent(id)}`);
}

export function fetchCustomGeo(query: string) {
  return fetchJSON<TrackerResponse>(`/api/geo/query?q=${encodeURIComponent(query)}`);
}

export interface SurpriseScoresResponse {
  flavor: "trend" | "consensus";
  window: string;
  scores: import("@macroscope/shared").SurpriseScore[];
  coverage_gap?: string;
  source: string;
  updated_at: string;
  delayed: boolean;
}

export function fetchSurpriseScores(flavor: "trend" | "consensus" = "trend") {
  return fetchJSON<SurpriseScoresResponse>(`/api/surprise?flavor=${flavor}`);
}

export function fetchSurpriseCountry(country: string, flavor: "trend" | "consensus" = "trend") {
  return fetchJSON<import("@macroscope/shared").SurpriseScore>(`/api/surprise/${country}?flavor=${flavor}`);
}

export interface RegimeResponse {
  regimes: import("@macroscope/shared").RegimeState[];
  coverage_gaps: string[];
  source: string;
  updated_at: string;
  delayed: boolean;
}

export function fetchRegime() {
  return fetchJSON<RegimeResponse>("/api/regime");
}

export function fetchRegimeCountry(country: string) {
  return fetchJSON<import("@macroscope/shared").RegimeState | { coverage_gap: string }>(`/api/regime/${country}`);
}

export interface BanksResponse {
  stances: import("@macroscope/shared").BankStance[];
  source: string;
  updated_at: string;
  delayed: boolean;
}

export interface DivergenceResponse {
  matrix: import("@macroscope/shared").Divergence[];
  source: string;
  updated_at: string;
  delayed: boolean;
}

export function fetchBanks() {
  return fetchJSON<BanksResponse>("/api/banks");
}

export function fetchDivergence() {
  return fetchJSON<DivergenceResponse>("/api/banks/divergence");
}

export interface CommunicationResponse {
  speeches: import("@macroscope/shared").SpeechScore[];
  committee_indices: Record<string, { days30: number; days90: number }>;
  tone_shifts: Array<{ bank: string; change: number; from: number; to: number }>;
  coverage_gap?: string;
  source: string;
  updated_at: string;
  delayed: boolean;
}

export function fetchCommunication() {
  return fetchJSON<CommunicationResponse>("/api/communication");
}

export interface BriefResponse {
  persona: import("@macroscope/shared").Persona;
  session: import("@macroscope/shared").BriefSession;
  headline: string;
  regime_note: string;
  cb_note: string;
  communication_note: string;
  liquidity_note: string;
  reads: import("@macroscope/shared").BriefRead[];
  watchlist: import("@macroscope/shared").BriefWatchlistItem[];
  coverage_gaps: string[];
  source: string;
  updated_at: string;
  delayed: boolean;
  educational: true;
}

export function fetchBrief(persona: string, session: string) {
  return fetchJSON<BriefResponse>(`/api/brief?persona=${encodeURIComponent(persona)}&session=${encodeURIComponent(session)}`);
}
